/** @jsxImportSource @emotion/react */
import React, { memo } from 'react';
import { EConnectorIdentifier, EStationPricingOption } from '../../../../api/types';
import Badge, { EBadgeType } from '../../../../components/badge/badge.component';
import ConnectorStatus from '../../../../components/connector-status/connector-status.component';
import setConnectorStyles from './set-connector.styles';
import { IStationConnector, IStation } from '../../../../api/station/types';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { getConnectorTypes, normalizeConnectorType } from '../../utils/getConnectorTypes';
import { getStationCurrency } from '../../../../utils/station';
import { shouldShowShortCode } from '../../../../utils/connector-identifier';

interface ConnectorInfoContainerProps {
    isUnavailable: boolean;
    connector?: IStationConnector & {
        station: IStation;
        connectorNumber: string;
    };
    isFree: boolean;
    station: IStation;
    showIdleFeeInfo?: boolean;
    connectorIdentifier?: EConnectorIdentifier;
    showShortCode?: boolean;
}

function ConnectorInfoContainer(props: ConnectorInfoContainerProps) {
    const {
        isUnavailable,
        connector,
        isFree,
        station,
        showIdleFeeInfo,
        connectorIdentifier,
        showShortCode: showShortCodeProp,
    } = props;
    const intl = useIntl();
    const connectorTariff = (connector as any)?.tariff;

    const typeData = useMemo(() => {
        const connectorTypes = getConnectorTypes();
        const normalizedType = normalizeConnectorType(connector?.type);

        return connectorTypes[normalizedType] || connectorTypes.default;
    }, [connector?.type]);

    const currency = getStationCurrency(station);

    const showShortCode =
        showShortCodeProp ?? shouldShowShortCode(connectorIdentifier);

    const shortCode = connector?.qrCode?.shortCode;

    return (
        <div css={setConnectorStyles.rightSideTopBlock}>
            <div css={setConnectorStyles.connectorInfoContainer}>
                <div css={setConnectorStyles.row}>
                    <div css={setConnectorStyles.connectorStatusContainer}>
                        {connector?.status ? (
                            <ConnectorStatus
                                connectorStatus={connector?.status}
                                stationStatus={station.status}
                                isUnavailable={isUnavailable}
                            />
                        ) : null}
                        {isFree && <Badge type={EBadgeType.FREE_CONNECTOR} />}
                    </div>
                    {showShortCode && shortCode && (
                        <div css={setConnectorStyles.connectorShortCode}>{shortCode}</div>
                    )}
                </div>
                <div css={[setConnectorStyles.connectorInfo]}>
                    <div css={setConnectorStyles.connectorInfoLabel}>
                        {intl.formatMessage({ id: 'station.maxPower' })}
                    </div>
                    <div css={setConnectorStyles.connectorInfoValue}>
                        {connector?.maxPower} {intl.formatMessage({ id: 'unit.kW' })}
                    </div>
                </div>

                {isFree ? (
                    <div css={setConnectorStyles.connectorInfo}>
                        <div css={setConnectorStyles.connectorInfoLabel}>
                            {intl.formatMessage({ id: 'payment.free' })}{' '}
                        </div>
                    </div>
                ) : (
                    <>
                        {connectorTariff?.pricingOption === EStationPricingOption.kwh ? (
                            <div css={setConnectorStyles.connectorInfo}>
                                <div css={setConnectorStyles.connectorInfoLabel}>
                                    {intl.formatMessage({ id: 'connector.info.per' })}{' '}
                                    {intl.formatMessage({ id: 'unit.kWh' })}
                                </div>
                                <div css={setConnectorStyles.connectorInfoValue}>
                                    {Number((connectorTariff?.priceForKwh ?? 0) / 100).toFixed(2)}{' '}
                                    {currency}
                                </div>
                            </div>
                        ) : (
                            <div css={setConnectorStyles.connectorInfo}>
                                <div css={setConnectorStyles.connectorInfoLabel}>
                                    {intl.formatMessage({ id: 'connector.info.per' })}{' '}
                                    {intl.formatMessage({ id: 'unit.h' })}
                                </div>
                                <div css={setConnectorStyles.connectorInfoValue}>
                                    {Number((connectorTariff?.priceForHour ?? 0) / 100).toFixed(2)}{' '}
                                    {currency}
                                </div>
                            </div>
                        )}
                        {showIdleFeeInfo && connector?.tariff?.idleFee?.enabled && (
                            <>
                                <div css={setConnectorStyles.connectorInfo}>
                                    <div css={setConnectorStyles.connectorInfoLabel}>
                                        {intl.formatMessage({ id: 'connector.info.idleFee' })}
                                    </div>
                                    <div css={setConnectorStyles.connectorInfoValue}>
                                        {Number((connector?.tariff?.idleFee?.price ?? 0) / 100).toFixed(
                                            2
                                        )}{' '}
                                        {currency}/{intl.formatMessage({ id: 'min' })}
                                    </div>
                                </div>
                                <div css={setConnectorStyles.connectorInfo}>
                                    <div css={setConnectorStyles.connectorInfoLabel}>
                                        {intl.formatMessage({ id: 'connector.info.gracePeriod' })}
                                    </div>
                                    <div css={setConnectorStyles.connectorInfoValue}>
                                        {connector?.tariff?.idleFee?.graceMinutes}{' '}
                                        {intl.formatMessage({ id: 'min' })}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                }}>
                <div css={setConnectorStyles.connectorIcon}>{typeData.icon}</div>
                <div css={setConnectorStyles.connectorTypeName}>{typeData.name}</div>
            </div>
        </div>
    );
}

export default memo(ConnectorInfoContainer);
